import React from 'react';
import { ImageBrokenIcon, CheckCircleIcon, NoteBlankIcon } from '@/components/icons';

interface StatusCardProps {
  message?: string;
  description?: string;
  variant?: 'default' | 'error' | 'success';
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const VARIANTS = {
  default: {
    container: 'border-border/50 bg-card',
    icon: 'text-muted-foreground',
    title: 'text-foreground',
    description: 'text-muted-foreground',
  },
  error: {
    container: 'border-destructive/20',
    icon: 'text-destructive',
    title: 'text-destructive',
    description: 'text-destructive',
  },
  success: {
    container: 'border-emerald-500/20 bg-emerald-50/30 dark:bg-emerald-950/20',
    icon: 'text-emerald-600 dark:text-emerald-400',
    title: 'text-emerald-900 dark:text-emerald-100',
    description: 'text-emerald-700/90 dark:text-emerald-300/90',
  },
};

const Icons = {
  default: NoteBlankIcon,
  error: ImageBrokenIcon,
  success: CheckCircleIcon,
};

export function StatusCard({ message, description, variant = 'default', icon, children }: StatusCardProps = {}) {
  const styles = VARIANTS[variant];
  const Icon = Icons[variant];

  return (
    <div className={`mt-8 overflow-hidden rounded-xl border p-10 text-center shadow-sm md:p-20 ${styles.container}`}>
      <div className="mx-auto max-w-2xl">
        {icon ?? <Icon size={64} weight="thin" className={`mx-auto mb-4 ${styles.icon}`} />}

        {message && <p className={`mb-4 text-lg font-bold md:text-xl leading-relaxed ${styles.title}`}>{message}</p>}

        {description && (
          <p className={`mx-auto max-w-xl text-base md:text-lg leading-relaxed ${styles.description}`}>{description}</p>
        )}

        {children && <div className="mt-6 flex flex-wrap justify-center gap-3">{children}</div>}
      </div>
    </div>
  );
}
